import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { Meta, templateContent } from '@/js/util';
import Util from '@/js/editor/util';

const arrayWrap = (object) => (Array.isArray(object) ? object : new Array(object));

class BoardLoading {
  static get element() {
    return document.querySelector('.board-loading');
  }

  static show() {
    this.element.style.display = 'flex';
  }

  static hide() {
    this.element.style.display = 'none';
  }
}

class EditorElements {
  static get snapshotsFrame() {
    return document.querySelector('turbo-frame#editor_snapshots_dropdown');
  }

  static set snapshotsFrameSrc(value) {
    this.snapshotsFrame.setAttribute('src', value);
  }

  static get boardNameContainer() {
    return document.querySelector('[data-editor-content="board-name"]');
  }

  static set boardName(value) {
    this.boardNameContainer.textContent = value;
  }

  static get backButton() {
    return document.querySelector('[data-editor-button="save-and-exit"]');
  }

  static set backButtonURL(value) {
    this.backButton.setAttribute('href', value);
  }
}

export default class extends Controller {
  static targets = ['board', 'boards', 'chevron', 'loadingBoardTemplate'];
  static outlets = ['moodboard--drawer--learn-button'];
  static classes = ['currentBoard'];
  static values = {
    isPanelOpened: Boolean,
    currentBoardId: Number,
    reorderPath: String,
  };

  connect() {
    this.connected = true;
    this.dragging = false;
    this.selectedBoardTarget = null;

    this.#showDropdownTriggers(this.boardTargets);
    this.#setupReordering();
    this.dispatchBoardChangedEvent();
  }

  boardTargetDisconnected(element) {
    if (this.selectedBoardTarget === element) {
      this.selectedBoardTarget = null;
    }
  }

  trackSwitch() {
    if (this.currentBoardIdValue !== parseInt(window.ed.boardId)) {
      window.ed.featureActionsService.add('boards_panel_design_switched');
    }
  }

  boardTargetConnected(element) {
    this.#showDropdownTriggers(element);

    if (!this.connected) {
      return;
    }

    if (element.getAttribute('data-switch-to') !== 'true') {
      return;
    }

    element.setAttribute('data-switch-to', 'false');

    if (!this.#allowedToSwitch) {
      return;
    }

    this.currentBoardIdValue = element.getAttribute('data-board-id');
  }

  dispatchBoardChangedEvent() {
    this.dispatch('board-changed', { detail: { boardId: this.currentBoardIdValue } });
  }

  isPanelOpenedValueChanged(value) {
    this.element.classList.toggle('translate-y-[calc(100%-18px)]', !value);
    this.chevronTarget.classList.toggle('rotate-180', value);

    if (this.hasMoodboardDrawerLearnButtonOutlet) {
      this.moodboardDrawerLearnButtonOutlet.adjustForBoardsPane(value);
    }

    this.dispatch('toggled', { detail: { open: value }, target: document });
  }

  currentBoardIdValueChanged(boardId) {
    if (!this.connected) {
      return;
    }

    BoardLoading.show();

    let currentBoardEl;
    this.boardTargets.forEach((element) => {
      if (element.getAttribute('data-board-type') !== 'board') {
        return;
      }

      const isCurrent = parseInt(element.getAttribute('data-board-id')) === boardId;
      if (isCurrent) {
        currentBoardEl = element;
      }
      const triggerElement = element.querySelector('button[type="submit"]');
      this.currentBoardClasses.forEach((currentClass) => {
        triggerElement.classList.toggle(currentClass, isCurrent);
      });
    });

    window.history.pushState({}, '', currentBoardEl.getAttribute('action'));

    window.ed.boardId = boardId;
    window.ed._io.clearBoard();
    window.ed._guiZoom.fitToScreen();
    window.ed._io.loadBoard();

    this.#updateUI(currentBoardEl);
    this.dispatchBoardChangedEvent();
  }

  /**
   * @param {CustomEvent} e
   */
  addLoadingTemplate({ detail }) {
    this.isPanelOpenedValue = true;
    const { domIds } = detail;
    const lastBoard = domIds.reduce((_, domId) => {
      const board = templateContent(this.loadingBoardTemplateTarget);
      board.id = domId;
      this.boardsTarget.appendChild(board);
      return board;
    }, null);
    lastBoard.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  }

  /**
   * @param {MouseEvent} e
   */
  deleteConfirmation(e) {
    const { target } = e;
    const boardId = target.getAttribute('data-board-id');
    const boardType = target.getAttribute('data-board-type').replace('_', '');

    this.selectedBoardTarget = this.boardTargets.find(
      (el) =>
        el.getAttribute('data-board-type') === boardType &&
        el.getAttribute('data-board-id') === boardId
    );
    if (!this.selectedBoardTarget) {
      return;
    }

    $('#board_confirm_deletion').modal('open');
  }

  delete() {
    const selectedBoard = this.selectedBoardTarget;
    if (!selectedBoard) {
      return;
    }

    const isCurrent =
      selectedBoard.getAttribute('data-board-type') === 'board' &&
      this.currentBoardIdValue === parseInt(selectedBoard.getAttribute('data-board-id'));

    if (isCurrent) {
      BoardLoading.show();
    }

    const moodboardIds = this.#allMoodboardIds;
    const url = this.selectedBoardTarget.getAttribute('data-destroy-board-path');
    const responseKind = 'turbo-stream';
    const request = new FetchRequest('DELETE', url, { responseKind });

    request
      .perform()
      .then(() => {
        if (!isCurrent) {
          return;
        }

        if (moodboardIds.length === 1) {
          return this.#createBlankBoard();
        }

        const currentBoardIndex = moodboardIds.indexOf(selectedBoard.getAttribute('data-board-id'));
        const newBoardIndex = currentBoardIndex === 0 ? 1 : currentBoardIndex - 1;
        this.currentBoardIdValue = moodboardIds[newBoardIndex];
      })
      .catch((error) => {
        isCurrent && BoardLoading.hide();
        console.error(error);
      });
  }

  open() {
    this.isPanelOpenedValue = true;
  }

  toggleListPanel() {
    if (!this.isPanelOpenedValue) {
      window.ed.featureActionsService.add('boards_panel_opened');
    }

    this.isPanelOpenedValue = !this.isPanelOpenedValue;
  }

  /**
   * @param {MouseEvent|TouchEvent} e
   */
  switchBoard(e) {
    if (this.dragging) {
      e.preventDefault();
      return;
    }

    const { currentTarget } = e;

    if (currentTarget.parentElement.getAttribute('data-board-type') === 'fpview') {
      return;
    } else {
      e.preventDefault();
    }

    if (!this.#allowedToSwitch) {
      return;
    }

    this.currentBoardIdValue = currentTarget.parentElement.getAttribute('data-board-id');
  }

  #showDropdownTriggers(elements) {
    if (!Util.isTouchScreen()) {
      return;
    }

    for (const element of arrayWrap(elements)) {
      element
        .querySelector('button[data-controller="moodboard--dropdown-trigger"]')
        ?.classList.remove('hidden');
    }
  }

  #setupReordering() {
    const start = (_event, _ui) => {
      this.dragging = true;
    };

    const stop = (_event, _ui) => {
      setTimeout(() => {
        window.ed.featureActionsService.add('boards_panel_design_reordered');
        this.dragging = false;
      }, 100);

      const combinedIds = this.boardTargets.map((element) => {
        const type = element.getAttribute('data-board-type');
        const id = element.getAttribute('data-board-id');

        return `${type}-${id}`;
      });

      const options = { body: { combined_ids: combinedIds } };
      const request = new FetchRequest('POST', this.reorderPathValue, options);
      request.perform().catch(console.error);
    };

    $(this.boardsTarget).sortable({
      start,
      stop,
      delay: 150,
      cancel: 'input,button[type="button"]',
    });
  }

  #updateUI(currentBoardEl) {
    Meta.set('board-id', this.currentBoardIdValue);
    EditorElements.snapshotsFrameSrc = currentBoardEl.getAttribute('data-snapshots-frame-path');
    EditorElements.boardName = currentBoardEl.getAttribute('title');
    EditorElements.backButtonURL = currentBoardEl.getAttribute('data-show-board-path');
  }

  get #allMoodboardIds() {
    return this.boardTargets.reduce((acc, element) => {
      if (element.getAttribute('data-board-type') !== 'board') {
        return acc;
      }
      acc.push(element.getAttribute('data-board-id'));
      return acc;
    }, []);
  }

  get #allowedToSwitch() {
    // beforeunload does not work with history API, so we kinda simulating it
    return !(window.onbeforeunload && !confirm('Changes that you made may not be saved.'));
  }

  #createBlankBoard() {
    document.querySelector('.board-types__type--2d').click();
  }
}
